import { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2, Star } from 'lucide-react';
import { getAdminJobs, createJob, updateJob, deleteJob, getAdminEmployers, getAdminCategories, getAdminLocations } from '../../services/api';
import toast from 'react-hot-toast';

const emptyJob = { title: '', company: '', location: '', type: 'Full Time', experience: '', salary: '', category: '', description: '', requirements: '', status: 'draft', is_featured: 0, employer_id: '' };

export default function AdminJobs() {
  const [jobs, setJobs] = useState([]);
  const [employers, setEmployers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyJob);

  const load = () => {
    Promise.all([getAdminJobs(), getAdminEmployers(), getAdminCategories(), getAdminLocations()]).then(([j, e, c, l]) => {
      setJobs(j.data);
      setEmployers(e.data);
      setCategories(c.data);
      setLocations(l.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  };
  useEffect(load, []);

  const openCreate = () => { setEditing(null); setForm(emptyJob); setShowModal(true); };
  const openEdit = (job) => { setEditing(job.id); setForm({ ...job, employer_id: job.employer_id || '' }); setShowModal(true); };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      const data = { ...form, is_featured: form.is_featured ? 1 : 0 };
      if (editing) { await updateJob(editing, data); toast.success('Job updated'); }
      else { await createJob(data); toast.success('Job created'); }
      setShowModal(false);
      load();
    } catch { toast.error('Failed to save'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this job?')) return;
    try { await deleteJob(id); toast.success('Deleted'); load(); } catch { toast.error('Failed'); }
  };

  const togglePublish = async (job) => {
    await updateJob(job.id, { status: job.status === 'published' ? 'draft' : 'published' });
    toast.success(job.status === 'published' ? 'Unpublished' : 'Published');
    load();
  };

  const toggleFeatured = async (job) => {
    await updateJob(job.id, { is_featured: job.is_featured == 1 ? 0 : 1 });
    toast.success(job.is_featured == 1 ? 'Removed from featured' : 'Marked as featured');
    load();
  };

  const handleEmployerChange = (empId) => {
    const emp = employers.find(e => e.id === empId);
    setForm({ ...form, employer_id: empId, company: emp ? emp.name : form.company });
  };

  if (loading) return <div className="loading"><div className="spinner"></div></div>;

  return (
    <>
      <div className="admin-table-wrap">
        <div className="admin-table-header">
          <h2>Manage Jobs ({jobs.length})</h2>
          <button className="btn-create" onClick={openCreate}><Plus size={18} /> Create Job</button>
        </div>
        {jobs.length === 0 ? (
          <div className="empty-state"><h3>No jobs created yet</h3></div>
        ) : (
          <table className="admin-table">
            <thead><tr><th>Title</th><th>Company</th><th>Location</th><th>Type</th><th>Featured</th><th>Status</th><th>Actions</th></tr></thead>
            <tbody>
              {jobs.map(job => (
                <tr key={job.id}>
                  <td style={{ fontWeight: 600 }}>{job.title}</td>
                  <td>{job.employer_name || job.company}</td>
                  <td>{job.location}</td>
                  <td>{job.type}</td>
                  <td>
                    <Star size={18} fill={job.is_featured == 1 ? '#f59e0b' : 'none'} color={job.is_featured == 1 ? '#f59e0b' : '#cbd5e1'}
                      style={{ cursor: 'pointer' }} onClick={() => toggleFeatured(job)} />
                  </td>
                  <td>
                    <span className={`status-badge status-${job.status}`} onClick={() => togglePublish(job)} style={{ cursor: 'pointer' }}>
                      {job.status}
                    </span>
                  </td>
                  <td>
                    <div className="table-actions">
                      <button className="btn-edit" onClick={() => openEdit(job)}><Pencil size={14} /> Edit</button>
                      <button className="btn-delete" onClick={() => handleDelete(job.id)}><Trash2 size={14} /> Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h2>{editing ? 'Edit Job' : 'Create New Job'}</h2>
            <form onSubmit={handleSave}>
              <div className="form-row">
                <div className="form-group">
                  <label>Job Title *</label>
                  <input type="text" value={form.title} onChange={e => setForm({...form, title: e.target.value})} required />
                </div>
                <div className="form-group">
                  <label>Employer</label>
                  <select value={form.employer_id} onChange={e => handleEmployerChange(e.target.value)}>
                    <option value="">Select employer</option>
                    {employers.map(emp => <option key={emp.id} value={emp.id}>{emp.name}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Company Name {form.employer_id ? '(auto-filled)' : '*'}</label>
                  <input type="text" value={form.company} onChange={e => setForm({...form, company: e.target.value})} required />
                </div>
                <div className="form-group">
                  <label>Location *</label>
                  <select value={form.location} onChange={e => setForm({...form, location: e.target.value})} required>
                    <option value="">Select location</option>
                    {locations.filter(l => l.is_active == 1).map(l => <option key={l.id} value={l.name}>{l.name}{l.state ? `, ${l.state}` : ''}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Job Type</label>
                  <select value={form.type} onChange={e => setForm({...form, type: e.target.value})}>
                    <option>Full Time</option>
                    <option>Part Time</option>
                    <option>Contract</option>
                    <option>Internship</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Category</label>
                  <select value={form.category} onChange={e => setForm({...form, category: e.target.value})}>
                    <option value="">Select category</option>
                    {categories.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Experience</label>
                  <input type="text" placeholder="e.g. 3-5 Years" value={form.experience} onChange={e => setForm({...form, experience: e.target.value})} />
                </div>
                <div className="form-group">
                  <label>Salary</label>
                  <input type="text" placeholder="e.g. 8-12 LPA" value={form.salary} onChange={e => setForm({...form, salary: e.target.value})} />
                </div>
              </div>
              <div className="form-group">
                <label>Job Description</label>
                <textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} />
              </div>
              <div className="form-group">
                <label>Requirements</label>
                <textarea value={form.requirements} onChange={e => setForm({...form, requirements: e.target.value})} style={{ minHeight: 80 }} />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Status</label>
                  <select value={form.status} onChange={e => setForm({...form, status: e.target.value})}>
                    <option value="draft">Draft</option>
                    <option value="published">Published</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Featured</label>
                  <select value={form.is_featured} onChange={e => setForm({...form, is_featured: parseInt(e.target.value)})}>
                    <option value={0}>No</option>
                    <option value={1}>Yes - Show on Homepage</option>
                  </select>
                </div>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-cancel" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn-save">{editing ? 'Update Job' : 'Create Job'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
